import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';
import '../styles/Contacts.css';

function Contacts({ user, onContactClick, onProfileClick, refreshUsers, refreshNotificationCount, socket, initialTab }) {
  const [contacts, setContacts] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [requestedSuggestionUsernames, setRequestedSuggestionUsernames] = useState([]);
  const [contactsSearchQuery, setContactsSearchQuery] = useState('');
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [activeTab, setActiveTab] = useState(initialTab === 'pending' ? 'requests' : 'contacts'); // 'contacts', 'requests', 'add'
  const [loading, setLoading] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [message, setMessage] = useState('');
  const [contactToRemove, setContactToRemove] = useState(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  // Cache of privacy-filtered profile data (id -> { profileImage, ... })
  const [privacyProfiles, setPrivacyProfiles] = useState({});

  const normalizeSuggestions = (usersList, excludedUsernames = requestedSuggestionUsernames) => {
    const excludedSet = new Set(excludedUsernames || []);
    return (usersList || [])
      .filter(candidate => !excludedSet.has(candidate.username))
      .map(candidate => ({
        ...candidate,
        mutualFriendsCount: Number(candidate?.mutualFriendsCount) || 0
      }));
  };

  const getMutualFriendsLabel = (count) => {
    if (!count) return '';
    return count === 1 ? '1 mutual friend' : `${count} mutual friends`;
  };

  const buildFallbackSuggestions = async (excludedUsernames = requestedSuggestionUsernames) => {
    const [usersResponse, contactsResponse] = await Promise.all([
      axios.get(`${config.apiUrl}/api/auth/users`),
      axios.get(`${config.apiUrl}/api/contacts/${user.id}`)
    ]);

    const acceptedContactIds = new Set((contactsResponse.data || []).map(contact => contact.id));
    const incomingPendingIds = new Set((pendingRequests || []).map(request => request.requesterId));

    const fallbackList = (usersResponse.data || [])
      .filter(candidate => candidate.id !== user.id && !acceptedContactIds.has(candidate.id))
      .sort((a, b) => a.username.localeCompare(b.username))
      .slice(0, 10)
      .map(candidate => ({
        id: candidate.id,
        username: candidate.username,
        profileImage: candidate.profileImage || null,
        contactStatus: incomingPendingIds.has(candidate.id) ? 'pending' : null,
        contactId: null
      }));

      return normalizeSuggestions(fallbackList, excludedUsernames);
  };

  // Fetch contacts
  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${config.apiUrl}/api/contacts/${user.id}`);
      setContacts(response.data);
      setFilteredContacts(response.data); // Initialize filtered contacts
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  // Filter contacts based on search query
  
  const filterContacts = (query) => {
    if (!query.trim()) {
      setFilteredContacts(contacts);
      return;
    }
    
    const filtered = contacts.filter(contact =>
      contact.username.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredContacts(filtered);
  };

  // Handle contacts search input change
  const handleContactsSearchChange = (e) => {
    const query = e.target.value;
    setContactsSearchQuery(query);
    filterContacts(query);
  };

  // Fetch pending requests
  const fetchPendingRequests = async () => {
    try {
      const response = await axios.get(`${config.apiUrl}/api/contacts/${user.id}/pending`);
      setPendingRequests(response.data);
    } catch (error) {
      console.error('Error fetching pending requests:', error);
    }
  };

  // Listen for real-time block status changes to immediately update avatars
  useEffect(() => {
    if (!socket || !user?.id) return;
    const handler = (data) => {
      let affectedUserId = null;
      if (data.type === 'blocked' && data.targetUserId) affectedUserId = data.targetUserId;
      if (data.type === 'blockedBy' && data.blockerId) affectedUserId = data.blockerId;
      if (data.type === 'unblocked' && data.targetUserId) affectedUserId = data.targetUserId;
      if (data.type === 'unblockedBy' && data.unblockerId) affectedUserId = data.unblockerId;
      if (!affectedUserId) return;

      // Clear cached privacy for affected user so avatar hides/shows immediately
      setPrivacyProfiles(prev => {
        const next = { ...prev };
        delete next[affectedUserId];
        return next;
      });

      // Refresh contacts and pending requests; also refresh chat list in parent
      fetchContacts();
      fetchPendingRequests();
      if (typeof refreshUsers === 'function') {
        refreshUsers(true);
      }
    };
    socket.on('blockStatusChanged', handler);
    return () => {
      socket.off('blockStatusChanged', handler);
    };
  }, [socket, user?.id]);

  // Search users
  const searchUsers = async (query) => {
    if (query.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${config.apiUrl}/api/contacts/search/${user.id}?query=${encodeURIComponent(query)}`);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Error searching users:', error);
      setMessage('Failed to search users. Please try again.');
      setTimeout(() => setMessage(''), 3000);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Get suggestions for users who are not already contacts
  const fetchSuggestedUsers = async (excludedUsernames = requestedSuggestionUsernames) => {
    try {
      setLoadingSuggestions(true);
      const response = await axios.get(`${config.apiUrl}/api/contacts/suggestions/${user.id}?limit=10`);
      setSuggestedUsers(normalizeSuggestions(response.data || [], excludedUsernames));
    } catch (error) {
      console.error('Error fetching suggested users:', error);
      const isSuggestionsRouteMissing = error?.response?.status === 404 ||
        error?.response?.data?.error === 'API route not found';

      if (isSuggestionsRouteMissing) {
        try {
          const fallbackSuggestions = await buildFallbackSuggestions(excludedUsernames);
          setSuggestedUsers(fallbackSuggestions);
        } catch (fallbackError) {
          console.error('Fallback suggestions fetch failed:', fallbackError);
          setSuggestedUsers([]);
        }
      } else {
        setSuggestedUsers([]);
      }
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // Send contact request
  const sendContactRequest = async (recipientUsername) => {
    setSendingRequest(true);
    try {
      const response = await axios.post(`${config.apiUrl}/api/contacts/request`, {
        requesterId: user.id,
        recipientUsername
      });

      const updatedRequestedUsernames = Array.from(
        new Set([...requestedSuggestionUsernames, recipientUsername])
      );

      setRequestedSuggestionUsernames(updatedRequestedUsernames);
      setSuggestedUsers(prev => prev.filter(suggestion => suggestion.username !== recipientUsername));
      
      setMessage('Contact request sent successfully!');
      
      // Refresh search results to show updated status
      if (searchQuery.trim().length >= 2) {
        await searchUsers(searchQuery);
      }
      
      // Refresh contacts and pending requests
      await fetchContacts();
      fetchPendingRequests();
      fetchSuggestedUsers(updatedRequestedUsernames);
      
      // Refresh notification count
      if (refreshNotificationCount) {
        refreshNotificationCount();
      }
      
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error sending contact request:', error);
      const errorMessage = error.response?.data?.error || 'Failed to send contact request';
      setMessage(errorMessage);
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setSendingRequest(false);
    }
  };

  // Accept contact request
  const acceptRequest = async (contactId) => {
    try {
      await axios.put(`${config.apiUrl}/api/contacts/${contactId}/accept`, {
        userId: user.id
      });
      setMessage('Contact request accepted!');
      
      // Refresh all relevant data
      fetchPendingRequests();
      await fetchContacts(); // Wait for contacts to be fetched
      fetchSuggestedUsers();
      refreshUsers(); // Refresh the main chat list
      
      // Refresh search results if search is active
      if (searchQuery.trim().length >= 2) {
        await searchUsers(searchQuery);
      }
      
      // Refresh notification count
      if (refreshNotificationCount) {
        refreshNotificationCount();
      }
      
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error accepting request:', error);
      setMessage(error.response?.data?.error || 'Failed to accept request');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  // Reject contact request
  const rejectRequest = async (contactId) => {
    try {
      await axios.put(`${config.apiUrl}/api/contacts/${contactId}/reject`, {
        userId: user.id
      });
      setMessage('Contact request rejected');
      
      // Refresh pending requests
      fetchPendingRequests();
      fetchSuggestedUsers();
      
      // Refresh search results if search is active
      if (searchQuery.trim().length >= 2) {
        await searchUsers(searchQuery);
      }
      
      // Refresh notification count
      if (refreshNotificationCount) {
        refreshNotificationCount();
      }
      
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error rejecting request:', error);
      setMessage(error.response?.data?.error || 'Failed to reject request');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  // Remove contact
  const removeContact = async (contactId) => {
    setContactToRemove(contactId);
    setShowRemoveConfirm(true);
  };

  // Confirm and remove contact
  const confirmRemoveContact = async () => {
    if (!contactToRemove) return;
    
    try {
      await axios.delete(`${config.apiUrl}/api/contacts/${contactToRemove}`, {
        data: { userId: user.id }
      });
      setMessage('Contact removed successfully');
      
      // Refresh all relevant data
      await fetchContacts(); // Wait for contacts to be fetched
      fetchSuggestedUsers();
      refreshUsers(); // Refresh the main chat list
      
      // Refresh search results if search is active
      if (searchQuery.trim().length >= 2) {
        await searchUsers(searchQuery);
      }
      
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error removing contact:', error);
      setMessage(error.response?.data?.error || 'Failed to remove contact');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setShowRemoveConfirm(false);
      setContactToRemove(null);
    }
  };

  // Cancel contact removal
  const cancelRemoveContact = () => {
    setShowRemoveConfirm(false);
    setContactToRemove(null);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    searchUsers(query);
  };

  // Load data on component mount
  useEffect(() => {
    fetchContacts();
    fetchPendingRequests();
    fetchSuggestedUsers();
  }, [user.id]);

  // Update filtered contacts when contacts change
  useEffect(() => {
    filterContacts(contactsSearchQuery);
  }, [contacts]);

  // Fetch privacy-aware profiles for any visible users (contacts, pending requests, search results)
  useEffect(() => {
    if (!user?.id) return;
    // Gather all candidate IDs currently displayed
    const contactIds = filteredContacts.map(c => c.id);
    const requestIds = pendingRequests.map(r => r.id);
    const searchIds = searchResults.map(r => r.id);
    const suggestionIds = suggestedUsers.map(r => r.id);
    const allIds = Array.from(new Set([...contactIds, ...requestIds, ...searchIds, ...suggestionIds]));
    // Determine which IDs we still need to fetch
    const toFetch = allIds.filter(id => !privacyProfiles[id]);
    if (toFetch.length === 0) return;
    // Limit per batch to avoid flooding server
    const batch = toFetch.slice(0, 25);
    batch.forEach(id => {
      axios.get(`${config.apiUrl}/api/auth/profile/${id}?requestingUserId=${user.id}`)
        .then(res => {
          setPrivacyProfiles(prev => ({ ...prev, [id]: res.data }));
        })
        .catch(err => {
          console.error('Failed to fetch privacy profile for user', id, err);
          // Store a negative result to prevent refetch loops
          setPrivacyProfiles(prev => ({ ...prev, [id]: { profileImage: null } }));
        });
    });
  }, [filteredContacts, pendingRequests, searchResults, suggestedUsers, user, privacyProfiles]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'accepted':
        return <span className="status-badge accepted">Contact</span>;
      case 'pending':
        return <span className="status-badge pending">Pending</span>;
      default:
        return null;
    }
  };

  const canSendContactRequest = (status) => !status || status === 'rejected';


  return (
    <div className="contacts-container">
      {/* Header */}
      <div className="contacts-header">
        <h2>Contacts</h2>
        <div className="contacts-tabs">
          <button 
            className={`tab ${activeTab === 'contacts' ? 'active' : ''}`}
            onClick={() => setActiveTab('contacts')}
          >
            Contacts ({contactsSearchQuery ? filteredContacts.length : contacts.length})
          </button>
          <button 
            className={`tab ${activeTab === 'requests' ? 'active' : ''}`}
            onClick={() => setActiveTab('requests')}
          >
            Requests ({pendingRequests.length})
          </button>
          <button 
            className={`tab ${activeTab === 'add' ? 'active' : ''}`}
            onClick={() => setActiveTab('add')}
          >
            Add Contact
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`message ${message.includes('successfully') || message.includes('accepted') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      {/* Contacts Tab */}
      {activeTab === 'contacts' && (
        <div className="contacts-list">
          {/* Search input for contacts */}
          <div className="search-container">
            <input
              type="text"
              placeholder="Search contacts by username..."
              value={contactsSearchQuery}
              onChange={handleContactsSearchChange}
              className="search-input"
            />
          </div>
          
          {filteredContacts.length === 0 && contacts.length === 0 ? (
            <div className="empty-state">
              <p>No contacts yet. Add some contacts to start chatting!</p>
            </div>
          ) : filteredContacts.length === 0 && contactsSearchQuery ? (
            <div className="empty-state">
              <p>No contacts found matching "{contactsSearchQuery}"</p>
            </div>
          ) : (
            filteredContacts.map(contact => (
              <div key={contact.id} className="contact-item">
                <div className="contact-avatar" onClick={() => onProfileClick && onProfileClick(contact.id)} style={{cursor: 'pointer'}}>
                  {(() => {
                    const privacyImage = privacyProfiles[contact.id]?.profileImage || null;
                    if (privacyImage) {
                      return (
                        <img
                          src={`${config.apiUrl}${privacyImage}`}
                          alt={contact.username}
                          className="contact-profile-image"
                        />
                      );
                    }
                    return (
                      <div className="contact-avatar-placeholder notranslate">
                        {contact.username.charAt(0).toUpperCase()}
                      </div>
                    );
                  })()}
                </div>
                <div className="contact-info">
                  <div className="contact-name notranslate" onClick={() => onProfileClick && onProfileClick(contact.id)} style={{cursor: 'pointer'}}>{contact.username}</div>
                  <div className="contact-status">Contact</div>
                </div>
                <div className="contact-actions">
                  <button 
                    className="action-btn chat-btn"
                    onClick={() => onContactClick(contact.id)}
                    title="Start chat"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                    </svg>
                  </button>
                  <button 
                    className="action-btn remove-btn"
                    onClick={() => removeContact(contact.contactId)}
                    title="Remove contact"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18"/>
                      <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6"/>
                      <path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                    </svg>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Requests Tab */}
      {activeTab === 'requests' && (
        <div className="requests-list">
          {pendingRequests.length === 0 ? (
            <div className="empty-state">
              <p>No pending contact requests</p>
            </div>
          ) : (
            pendingRequests.map(request => (
              <div key={request.id} className="request-item">
                <div className="request-avatar">
                  {(() => {
                    const privacyImage = privacyProfiles[request.id]?.profileImage || null;
                    if (privacyImage) {
                      return (
                        <img
                          src={`${config.apiUrl}${privacyImage}`}
                          alt={request.username}
                          className="request-profile-image"
                        />
                      );
                    }
                    return (
                      <div className="request-avatar-placeholder notranslate">
                        {request.username.charAt(0).toUpperCase()}
                      </div>
                    );
                  })()}
                </div>
                <div className="request-info">
                  <div className="request-name notranslate">{request.username}</div>
                  <div className="request-status">Wants to add you as a contact</div>
                  <div className="request-time">
                    {new Date(request.createdAt).toLocaleDateString()}
                  </div>
                </div>
                <div className="request-actions">
                  <button 
                    className="action-btn accept-btn"
                    onClick={() => acceptRequest(request.id)}
                    title="Accept request"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 6L9 17l-5-5"/>
                    </svg>
                  </button>
                  <button 
                    className="action-btn reject-btn"
                    onClick={() => rejectRequest(request.id)}
                    title="Reject request"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 6L6 18"/>
                      <path d="M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Add Contact Tab */}
      {activeTab === 'add' && (
        <div className="add-contact">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search users by username..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>

          {!searchQuery.trim() && (
            <div className="suggestions-section">
              <div className="suggestions-title">Suggestions</div>
              {loadingSuggestions ? (
                <div className="loading-state suggestions-loading">
                  <p>Loading suggestions...</p>
                </div>
              ) : suggestedUsers.length === 0 ? (
                <div className="empty-state suggestions-empty-state">
                  <p>No suggestions available right now</p>
                </div>
              ) : (
                <div className="suggestions-list">
                  {suggestedUsers.map(user => (
                    <div key={user.id} className="search-result-item">
                      <div className="result-avatar">
                        {(() => {
                          const privacyImage = privacyProfiles[user.id]?.profileImage || null;
                          if (privacyImage) {
                            return (
                              <img
                                src={`${config.apiUrl}${privacyImage}`}
                                alt={user.username}
                                className="result-profile-image"
                              />
                            );
                          }
                          return (
                            <div className="result-avatar-placeholder notranslate">
                              {user.username.charAt(0).toUpperCase()}
                            </div>
                          );
                        })()}
                      </div>
                      <div className="result-info">
                        <div className="result-name notranslate">{user.username}</div>
                        {user.mutualFriendsCount > 0 && (
                          <div className="mutual-friends-text">{getMutualFriendsLabel(user.mutualFriendsCount)}</div>
                        )}
                        <div className="result-status">
                          {getStatusBadge(user.contactStatus)}
                        </div>
                      </div>
                      <div className="result-actions">
                        {canSendContactRequest(user.contactStatus) && (
                          <button
                            className="action-btn add-btn"
                            onClick={() => sendContactRequest(user.username)}
                            disabled={sendingRequest}
                            title="Send contact request"
                          >
                            {sendingRequest ? (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spinning">
                                <path d="M21 12a9 9 0 11-6.219-8.56"/>
                              </svg>
                            ) : (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 5v14"/>
                                <path d="M5 12h14"/>
                              </svg>
                            )}
                          </button>
                        )}
                        {user.contactStatus === 'pending' && (
                          <span className="status-text">Request pending</span>
                        )}
                        {user.contactStatus === 'accepted' && (
                          <span className="status-text">Already a contact</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {searchQuery.trim().length >= 2 && (
          <div className="search-results">
            {loading && searchQuery.trim().length >= 2 && (
              <div className="loading-state">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spinning">
                  <path d="M21 12a9 9 0 11-6.219-8.56"/>
                </svg>
                <p>Searching users...</p>
              </div>
            )}
            
            {!loading && searchQuery.trim().length >= 2 && searchResults.map(user => (
              <div key={user.id} className="search-result-item">
                <div className="result-avatar">
                  {(() => {
                    const privacyImage = privacyProfiles[user.id]?.profileImage || null;
                    if (privacyImage) {
                      return (
                        <img
                          src={`${config.apiUrl}${privacyImage}`}
                          alt={user.username}
                          className="result-profile-image"
                        />
                      );
                    }
                    return (
                      <div className="result-avatar-placeholder notranslate">
                        {user.username.charAt(0).toUpperCase()}
                      </div>
                    );
                  })()}
                </div>
                <div className="result-info">
                  <div className="result-name notranslate">{user.username}</div>
                  <div className="result-status">
                    {getStatusBadge(user.contactStatus)}
                  </div>
                </div>
                <div className="result-actions">
                  {canSendContactRequest(user.contactStatus) && (
                    <button 
                      className="action-btn add-btn"
                      onClick={() => sendContactRequest(user.username)}
                      disabled={sendingRequest}
                      title="Send contact request"
                    >
                      {sendingRequest ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spinning">
                          <path d="M21 12a9 9 0 11-6.219-8.56"/>
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 5v14"/>
                          <path d="M5 12h14"/>
                        </svg>
                      )}
                    </button>
                  )}
                  {user.contactStatus === 'pending' && (
                    <span className="status-text">Request pending</span>
                  )}
                  {user.contactStatus === 'accepted' && (
                    <span className="status-text">Already a contact</span>
                  )}
                </div>
              </div>
            ))}
            {!loading && searchQuery && searchResults.length === 0 && searchQuery.trim().length >= 2 && (
              <div className="empty-state">
                <p>No users found</p>
              </div>
            )}
          </div>
          )}
        </div>
      )}

      {/* Confirmation Modal for Remove Contact */}
      {showRemoveConfirm && (
        <div className="confirmation-modal">
          <div className="modal-content">
            <h3>Confirm Removal</h3>
            <p>Are you sure you want to remove this contact?</p>
            <div className="modal-actions">
              <button className="action-btn cancel-btn" onClick={cancelRemoveContact}>Cancel</button>
              <button className="action-btn confirm-btn" onClick={confirmRemoveContact}>Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Contacts; 